import { useCallback, useEffect } from 'react';
import { config } from '../config';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import { useStreak } from '../contexts/StreakContext';
import { useStelline } from '../contexts/StellineContext';
import { useLearnedTerms } from '../contexts/LearnedTermsContext';
import { useMilestones } from '../contexts/MilestoneContext';
import { getMilestoneResponseGeneration, publishMilestoneResponse } from '../utils/milestoneResponses';

const DevResourceCelebrationShortcuts = () => {
  const { queueResourceCelebration, queueStellineCelebration, queueStreakCelebration } = useResourceCelebration();
  const { streakData, setStreakData } = useStreak();
  const { setStellineData } = useStelline();
  const { setLearnedTermsData } = useLearnedTerms();
  const { previewDailyCircuit } = useMilestones();

  const showStreakCelebration = useCallback(() => {
    queueStreakCelebration({
      delta: 1,
      counterBefore: streakData?.currentStreak || 0,
    });
    setStreakData((currentStreak) => ({
      ...currentStreak,
      currentStreak: Math.max(0, Number(currentStreak?.currentStreak) || 0) + 1,
    }));
  }, [queueStreakCelebration, streakData, setStreakData]);

  const showStellineCelebration = useCallback((delta = 50) => {
    const safeDelta = Math.max(1, Math.trunc(Number(delta) || 50));
    queueStellineCelebration({
      delta: safeDelta,
    });
    setStellineData((currentStelline) => ({
      ...currentStelline,
      balance: Math.max(0, Number(currentStelline?.balance) || 0) + safeDelta,
      delta: safeDelta,
    }));
  }, [queueStellineCelebration, setStellineData]);

  const showLearnedTermsCelebration = useCallback((delta = 4) => {
    const safeDelta = Math.max(1, Math.trunc(Number(delta) || 4));
    queueResourceCelebration('learned_terms', {
      delta: safeDelta,
    });
    setLearnedTermsData((currentLearnedTerms) => ({
      ...currentLearnedTerms,
      count: Math.max(0, Number(currentLearnedTerms?.count) || 0) + safeDelta,
      delta: safeDelta,
    }));
  }, [queueResourceCelebration, setLearnedTermsData]);

  useEffect(() => {
    if (config.ENVIRONMENT !== 'development') return undefined;

    const previousCommands = window.gdpDevResourceCelebrations;
    window.gdpDevResourceCelebrations = {
      ...(previousCommands || {}),
      streak: showStreakCelebration,
      stelline: showStellineCelebration,
      learnedTerms: showLearnedTermsCelebration,
      dailyCircuit: previewDailyCircuit,
      milestone: (resource = 'streak', threshold = 7) => {
        const start = new Date();
        start.setUTCHours(0, 0, 0, 0);
        start.setUTCDate(start.getUTCDate() - start.getUTCDay());
        const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        publishMilestoneResponse({ app_resources: { metadata: {
          milestone_events: [{ id: `dev-milestone-${Date.now()}`, resource, threshold, value: threshold,
            achieved_at: new Date().toISOString(), scope_key: 'development',
            ...(resource === 'stelline' ? { period_start_at: start.toISOString(), period_end_at: end.toISOString() } : {}),
          }],
        } } }, getMilestoneResponseGeneration());
      },
    };

    const handleKeyPress = (event) => {
      // Ignore synthetic keydown events that do not include a string key.
      if (typeof event.key !== 'string' || !event.altKey || !event.ctrlKey) return;

      if (event.key === 's' && event.altKey && event.ctrlKey) {
        event.preventDefault();
        showStreakCelebration();
      }
      if (event.key.toLowerCase() === 'l' && event.altKey && event.ctrlKey) {
        event.preventDefault();
        showStellineCelebration(50);
      }
      if (event.key.toLowerCase() === 'p' && event.altKey && event.ctrlKey) {
        event.preventDefault();
        showLearnedTermsCelebration(4);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (previousCommands) {
        window.gdpDevResourceCelebrations = previousCommands;
      } else {
        delete window.gdpDevResourceCelebrations;
      }
    };
  }, [showStreakCelebration, showStellineCelebration, showLearnedTermsCelebration, previewDailyCircuit]);

  return null;
};

export default DevResourceCelebrationShortcuts;
